import { useMutation } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { faFilePdf } from '@fortawesome/pro-solid-svg-icons';
import { generateExports } from 'api/contacts/exports';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { GivebutterAxiosError } from 'types/error';

interface ExportBidCardsDialogProps extends AlertDialog.AlertDialogRootProps {}
const ExportBidCardsDialog = (props: ExportBidCardsDialogProps) => {
    const { children, ...rest } = props;
    const { success, error } = useToast();
    const { accountId } = useCurrentAccount();
    const { data: campaign } = useCurrentCampaign();
    const auctionId = Number(campaign.auction_id);

    const { isPending: isGeneratingReport, mutate: submit } = useMutation({
        mutationFn: () =>
            generateExports(accountId, {
                object: 'auction',
                resource: 'auction_bidders',
                template: 'auction.bidder-cards',
                report_type: 'pdf',
                object_id: auctionId,
            }),
        onSuccess: () => {
            success(
                <div className="flex flex-col">
                    <span className="font-medium">Export in progress</span>
                    <div>We'll send you an email once it's done.</div>
                </div>,
                { size: 'md' },
            );
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message || 'Something went wrong. Please try again.');
        },
    });

    return (
        <AlertDialog.Root type="info" {...rest}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content className="max-w-sm p-6">
                    <AlertDialog.Header>
                        <AlertDialog.Icon customIcon={faFilePdf} />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>Export auction bid cards</AlertDialog.Title>
                        <AlertDialog.Description>
                            This will export a PDF of bidder numbers ready for printing. Once your
                            export is ready, we'll send you a secure download link via email.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                        <AlertDialog.Action onClick={() => submit()} disabled={isGeneratingReport}>
                            Export
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const ExportBidCardsDialogTrigger = AlertDialog.Trigger;

const Root = ExportBidCardsDialog;
const Trigger = ExportBidCardsDialogTrigger;

export {
    Root,
    Trigger,
    //
    ExportBidCardsDialog,
    ExportBidCardsDialogTrigger,
};
